// Ported from packages/workflow-engine-upstream/ui/src/compare.ts at commit bebc382. Changed from the
// reference: every step field is compared (the reference compares five), so an edit of only
// timeoutMs, emits or another field still shows as a change before approval.
// Plan versions side by side (F8, design §8.13): what a version adds, drops or changes against the
// version it came from, and the short label each version tab carries.
import type { Plan, RunEvent } from './contracts';

type Step = Plan['steps'][number];

export interface StepChange {
  id: string;
  title: string;
  /** Which parts differ: title, kind, op, bindings, dependsOn, then any other step field, and order. */
  fields: string[];
}

export interface PlanDiff {
  from: number;
  to: number;
  added: Step[];
  removed: Step[];
  changed: StepChange[];
  kept: string[];
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The fields named first, in this order; the rest follow by name. */
const FIRST = ['title', 'kind', 'op', 'bindings', 'dependsOn'];

/** The differences between two versions of one plan, by step id. */
export function comparePlans(before: Plan, after: Plan): PlanDiff {
  const old = new Map(before.steps.map((s) => [s.id, s]));
  const ids = new Set(after.steps.map((s) => s.id));
  // The steps both versions have, in each version's order, so moving one shows as a change of order.
  const oldOrder = before.steps.filter((s) => ids.has(s.id)).map((s) => s.id);
  const newOrder = after.steps.filter((s) => old.has(s.id)).map((s) => s.id);
  const changed: StepChange[] = [];
  const kept: string[] = [];
  const added: Step[] = [];
  for (const s of after.steps) {
    const prev = old.get(s.id);
    if (!prev) {
      added.push(s);
      continue;
    }
    const keys = new Set([...FIRST, ...[...Object.keys(prev), ...Object.keys(s)].sort()].filter((k) => k !== 'id'));
    const fields: string[] = [...keys].filter((k) => !same(prev[k as keyof Step], s[k as keyof Step]));
    if (oldOrder.indexOf(s.id) !== newOrder.indexOf(s.id)) fields.push('order');
    if (fields.length) changed.push({ id: s.id, title: s.title, fields });
    else kept.push(s.id);
  }
  return { from: before.version, to: after.version, added, removed: before.steps.filter((s) => !ids.has(s.id)), changed, kept };
}

/** "new" or "changed" for a step of `plan` against its parent version, or null when it is unchanged or v1. */
export function stepMark(versions: Plan[], plan: Plan, stepId: string): 'new' | 'changed' | null {
  const parent = versions.find((p) => p.version === (plan.parentVersion ?? plan.version - 1));
  if (!parent || parent.version === plan.version) return null;
  const d = comparePlans(parent, plan);
  if (d.added.some((s) => s.id === stepId)) return 'new';
  return d.changed.some((c) => c.id === stepId) ? 'changed' : null;
}

/** The tab label's reason: "re-planned at step s3", "edit", "file sent", or the plan's own reason. */
export function versionLabel(plan: Plan, events: RunEvent[]): string {
  if (plan.version === 1 && !plan.parentVersion) return '';
  const replanned = events.find((e) => e.type === 'replanned' && e.planVersion === plan.version);
  if (replanned?.stepId) return `re-planned at step ${replanned.stepId}`;
  const reason = plan.reason ?? (replanned?.payload?.reason as string | undefined) ?? '';
  return reason.length > 40 ? `${reason.slice(0, 39)}…` : reason;
}
